// Four-player chess (free for all) on a 14×14 cross-shaped board.
// White sits at the bottom, Green on the left, Black at the top and Yellow on the right; turns go
// clockwise in that order. A player with no legal move on their turn (checkmate or stalemate) is
// out: their pieces stay as grey blocks that anyone may take. Kings are never captured.
// The last player standing wins.

export type Seat = 'w' | 'g' | 'b' | 'y';
export const SEATS: Seat[] = ['w', 'g', 'b', 'y'];
export const SEAT_NAMES: Record<Seat, string> = { w: 'White', g: 'Green', b: 'Black', y: 'Yellow' };

export type Kind4 = 'K' | 'Q' | 'R' | 'B' | 'N' | 'P';
/** `x` marks the grey pieces of a player who is out. */
export interface Piece4 { k: Kind4; c: Seat | 'x' }
export interface Move4 { from: number; to: number; promo?: Kind4 }
export interface Result4 { winner: Seat | null; reason: string }

export interface State4 {
  board: (Piece4 | null)[];
  turn: Seat;
  /** Players still in the game. */
  alive: Seat[];
  /** Players who are out, first out first. */
  out: { seat: Seat; reason: string }[];
  ply: number;
  last: Move4 | null;
  result: Result4 | null;
}

export const N4 = 14;
const CORNER = 3;
export const VALUE4: Record<Kind4, number> = { K: 0, Q: 9, R: 5, B: 3, N: 3, P: 1 };
/** After this many plies without a result the game is drawn (stops endless bot games). */
const MAX_PLY = 1200;

export const sq4 = (x: number, y: number) => y * N4 + x;
export const xy4 = (sq: number): [number, number] => [sq % N4, Math.floor(sq / N4)];
export const onBoard4 = (x: number, y: number) =>
  x >= 0 && y >= 0 && x < N4 && y < N4 && !((x < CORNER || x >= N4 - CORNER) && (y < CORNER || y >= N4 - CORNER));

/** Forward direction of each seat's pawns. */
export const FORWARD: Record<Seat, [number, number]> = { w: [0, 1], g: [1, 0], b: [0, -1], y: [-1, 0] };

/** How far a square is from the seat's own back rank (0 = back rank). */
function rankOf(seat: Seat, x: number, y: number) {
  return seat === 'w' ? y : seat === 'b' ? N4 - 1 - y : seat === 'g' ? x : N4 - 1 - x;
}
const PROMO_RANK = 7;

export function createGame4(): State4 {
  const board: (Piece4 | null)[] = new Array(N4 * N4).fill(null);
  const back: Kind4[] = ['R', 'N', 'B', 'Q', 'K', 'B', 'N', 'R'];
  // Black and Yellow have king and queen swapped, so opposite kings do not share a line.
  const swapped: Kind4[] = ['R', 'N', 'B', 'K', 'Q', 'B', 'N', 'R'];
  for (let i = 0; i < 8; i++) {
    const a = CORNER + i;
    board[sq4(a, 0)] = { k: back[i], c: 'w' };
    board[sq4(a, 1)] = { k: 'P', c: 'w' };
    board[sq4(a, N4 - 1)] = { k: swapped[i], c: 'b' };
    board[sq4(a, N4 - 2)] = { k: 'P', c: 'b' };
    board[sq4(0, a)] = { k: back[i], c: 'g' };
    board[sq4(1, a)] = { k: 'P', c: 'g' };
    board[sq4(N4 - 1, a)] = { k: swapped[i], c: 'y' };
    board[sq4(N4 - 2, a)] = { k: 'P', c: 'y' };
  }
  return { board, turn: 'w', alive: [...SEATS], out: [], ply: 0, last: null, result: null };
}

const KNIGHT: [number, number][] = [[1, 2], [2, 1], [2, -1], [1, -2], [-1, -2], [-2, -1], [-2, 1], [-1, 2]];
const DIAG: [number, number][] = [[1, 1], [1, -1], [-1, 1], [-1, -1]];
const ORTHO: [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1]];
const ALL8 = [...DIAG, ...ORTHO];

/** Squares the piece on `from` attacks (pawns: diagonal captures only). */
function attacks(s: State4, from: number, out: number[]) {
  const p = s.board[from];
  if (!p || p.c === 'x') return;
  const [x, y] = xy4(from);
  const ray = (dirs: [number, number][], slide: boolean) => {
    for (const [dx, dy] of dirs) {
      let cx = x + dx, cy = y + dy;
      while (onBoard4(cx, cy)) {
        out.push(sq4(cx, cy));
        if (!slide || s.board[sq4(cx, cy)]) break;
        cx += dx; cy += dy;
      }
    }
  };
  switch (p.k) {
    case 'N': ray(KNIGHT, false); break;
    case 'B': ray(DIAG, true); break;
    case 'R': ray(ORTHO, true); break;
    case 'Q': ray(ALL8, true); break;
    case 'K': ray(ALL8, false); break;
    case 'P': {
      const [fx, fy] = FORWARD[p.c];
      // The two diagonals ahead: forward plus either side.
      const sides: [number, number][] = fx === 0 ? [[1, fy], [-1, fy]] : [[fx, 1], [fx, -1]];
      ray(sides, false);
    }
  }
}

/** Is `sq` attacked by any player still in the game other than `seat`? */
export function attacked4(s: State4, sq: number, seat: Seat): boolean {
  const buf: number[] = [];
  for (let from = 0; from < s.board.length; from++) {
    const p = s.board[from];
    if (!p || p.c === 'x' || p.c === seat || !s.alive.includes(p.c)) continue;
    buf.length = 0;
    attacks(s, from, buf);
    if (buf.includes(sq)) return true;
  }
  return false;
}

const kingSq = (s: State4, seat: Seat) => s.board.findIndex((p) => p?.k === 'K' && p.c === seat);

export function inCheck4(s: State4, seat: Seat): boolean {
  const k = kingSq(s, seat);
  return k >= 0 && attacked4(s, k, seat);
}

function pseudo(s: State4, seat: Seat): Move4[] {
  const out: Move4[] = [];
  const buf: number[] = [];
  for (let from = 0; from < s.board.length; from++) {
    const p = s.board[from];
    if (!p || p.c !== seat) continue;
    const [x, y] = xy4(from);
    const add = (to: number) => {
      const [tx, ty] = xy4(to);
      if (p.k === 'P' && rankOf(seat, tx, ty) >= PROMO_RANK) out.push({ from, to, promo: 'Q' });
      else out.push({ from, to });
    };
    if (p.k === 'P') {
      const [fx, fy] = FORWARD[seat];
      const one = [x + fx, y + fy];
      if (onBoard4(one[0], one[1]) && !s.board[sq4(one[0], one[1])]) {
        add(sq4(one[0], one[1]));
        const two = [x + 2 * fx, y + 2 * fy];
        if (rankOf(seat, x, y) === 1 && onBoard4(two[0], two[1]) && !s.board[sq4(two[0], two[1])]) add(sq4(two[0], two[1]));
      }
      buf.length = 0;
      attacks(s, from, buf);
      for (const to of buf) { const t = s.board[to]; if (t && t.c !== seat) add(to); }
      continue;
    }
    buf.length = 0;
    attacks(s, from, buf);
    for (const to of buf) { const t = s.board[to]; if (!t || t.c !== seat) add(to); }
  }
  return out;
}

function move(s: State4, m: Move4): State4 {
  const board = s.board.slice();
  const p = board[m.from]!;
  board[m.to] = m.promo ? { k: m.promo, c: p.c } : p;
  board[m.from] = null;
  return { ...s, board, last: m, ply: s.ply + 1 };
}

/** Legal moves for the player to move: kings are never taken and your own king may not stay attacked. */
export function legal4(s: State4, seat: Seat = s.turn): Move4[] {
  if (s.result || !s.alive.includes(seat)) return [];
  return pseudo(s, seat).filter((m) => s.board[m.to]?.k !== 'K' && !inCheck4(move(s, m), seat));
}

export const sameMove4 = (a: Move4, b: Move4) => a.from === b.from && a.to === b.to;

function nextSeat(s: State4, after: Seat): Seat {
  const i = SEATS.indexOf(after);
  for (let k = 1; k <= 4; k++) {
    const c = SEATS[(i + k) % 4];
    if (s.alive.includes(c)) return c;
  }
  return after;
}

/** Take a player out: their pieces turn grey (kings disappear). */
function knockOut(s: State4, seat: Seat, reason: string): State4 {
  if (!s.alive.includes(seat)) return s;
  const board = s.board.map((p) => (p?.c === seat ? (p.k === 'K' ? null : { k: p.k, c: 'x' as const }) : p));
  const alive = s.alive.filter((c) => c !== seat);
  const out = [...s.out, { seat, reason }];
  let n: State4 = { ...s, board, alive, out };
  if (alive.length === 1) n.result = { winner: alive[0], reason };
  return n;
}

/** Hand the turn on, taking out every player who has no legal move when it is their turn. */
function passTurn(s: State4, from: Seat): State4 {
  let n = s;
  let seat = from;
  for (let guard = 0; guard < 5 && !n.result; guard++) {
    seat = nextSeat(n, seat);
    n = { ...n, turn: seat };
    if (legal4(n).length) break;
    n = knockOut(n, seat, inCheck4(n, seat) ? 'checkmate' : 'stalemate');
  }
  if (!n.result && n.ply >= MAX_PLY) {
    // Too long: the player with the most material left wins (a tie is a draw).
    const mat = (c: Seat) => n.board.reduce((sum, p) => sum + (p?.c === c ? VALUE4[p.k] : 0), 0);
    const ranked = [...n.alive].sort((a, b) => mat(b) - mat(a));
    const winner = ranked.length > 1 && mat(ranked[0]) === mat(ranked[1]) ? null : ranked[0];
    n = { ...n, result: { winner, reason: 'move limit' } };
  }
  return n;
}

export function apply4(s: State4, m: Move4): State4 {
  const legal = legal4(s).find((x) => sameMove4(x, m));
  if (!legal) throw new Error('Illegal move.');
  return passTurn(move(s, legal), s.turn);
}

/** A player resigns, runs out of time or leaves. The game goes on without them. */
export function eliminate4(s: State4, seat: Seat, reason: string): State4 {
  if (s.result || !s.alive.includes(seat)) return s;
  const n = knockOut(s, seat, reason);
  return n.result || n.turn !== seat ? n : passTurn(n, seat);
}

/** Final places, best first (the winner, then the players who lasted longest). */
export function placings4(s: State4): Seat[] {
  return [...s.alive, ...[...s.out].reverse().map((o) => o.seat)];
}

const FILES = 'abcdefghijklmn';
export const sqName4 = (sq: number) => { const [x, y] = xy4(sq); return FILES[x] + (y + 1); };

export function moveText4(s: State4, m: Move4): string {
  const p = s.board[m.from];
  const cap = s.board[m.to] ? 'x' : '-';
  return `${p && p.k !== 'P' ? p.k : ''}${sqName4(m.from)}${cap}${sqName4(m.to)}${m.promo ? '=' + m.promo : ''}`;
}

// ---------------------------------------------------------------- admin tools

export type Admin4 = { kind: 'nuke' } | { kind: 'win' } | { kind: 'spawn'; piece: Kind4; sq: number };

export function admin4(s: State4, seat: Seat, a: Admin4): State4 {
  if (s.result || !s.alive.includes(seat)) throw new Error('You are not in the game.');
  if (a.kind === 'spawn') {
    const [x, y] = xy4(a.sq);
    if (a.piece === 'K' || !onBoard4(x, y) || s.board[a.sq]) throw new Error('Pick an empty square.');
    const board = s.board.slice();
    board[a.sq] = { k: a.piece, c: seat };
    return { ...s, board };
  }
  let n = s;
  if (a.kind === 'nuke') n = { ...s, board: s.board.map((p) => (p?.c === seat && p.k === 'K' ? p : null)) };
  const reason = a.kind === 'nuke' ? 'nuke' : 'admin win';
  return { ...n, alive: [seat], out: [...n.out, ...n.alive.filter((c) => c !== seat).map((c) => ({ seat: c, reason }))], result: { winner: seat, reason } };
}

// ---------------------------------------------------------------- bot

/**
 * A quick bot: grabs material, avoids leaving the moved piece where it can be taken,
 * likes checks, promotions and the centre. `noise` adds randomness (0 = always the best it sees).
 */
export function botMove4(s: State4, noise = 1.5, rand: () => number = Math.random): Move4 | null {
  const seat = s.turn;
  const moves = legal4(s);
  if (!moves.length) return null;
  let best: Move4 | null = null;
  let bestScore = -Infinity;
  for (const m of moves) {
    const p = s.board[m.from]!;
    const victim = s.board[m.to];
    let score = victim ? (victim.c === 'x' ? 0.6 : VALUE4[victim.k]) * 10 : 0;
    if (m.promo) score += 80;
    const after = move(s, m);
    if (attacked4(after, m.to, seat)) score -= VALUE4[m.promo ?? p.k] * 9;
    // Did the move leave something else hanging? Count the worst attacked own piece.
    let worst = 0;
    for (let sq = 0; sq < after.board.length; sq++) {
      const q = after.board[sq];
      if (q?.c === seat && q.k !== 'K' && sq !== m.to && VALUE4[q.k] > worst && attacked4(after, sq, seat)) worst = VALUE4[q.k];
    }
    score -= worst * 4;
    for (const c of after.alive) if (c !== seat && inCheck4(after, c)) score += 3;
    const [x, y] = xy4(m.to);
    score += (7 - Math.abs(x - 6.5) - Math.abs(y - 6.5)) * 0.15;
    score += rand() * noise;
    if (score > bestScore) { bestScore = score; best = m; }
  }
  return best;
}
